import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams } from "react-router-dom";
import { Badge, Button, Col, Divider, Empty, List, Row, Spin, Layout, Tabs, Tag, Typography, message, Modal, Tooltip, Input, Space, Alert } from "antd";
import { 
    CheckCircleFilled, 
    CloseCircleFilled, 
    CodeOutlined, 
    FileTextOutlined, 
    UnorderedListOutlined, 
    ConsoleSqlOutlined, 
    ExperimentOutlined, 
    DeleteOutlined, 
    SyncOutlined, 
    SaveOutlined, 
    ReloadOutlined,
    UndoOutlined,
    PlusOutlined,
    EditOutlined,
    ToolOutlined
} from "@ant-design/icons";
import { AuthContext } from "../../../../config/AuthContext";
import JutgeColors from "../../../../styles/JutgeColors";
import Editor from "@monaco-editor/react";
import { GetPracticaUsuarisEnviaments } from "../../../../dao/jutge/GetPracticaUsuarisEnviaments";
import { GetEnviamentFiles } from "../../../../dao/jutge/GetEnviamentFiles";
import { DeleteJutgeEnviament } from "../../../../dao/jutge/DeleteJutgeEnviament";
import { GetPractica } from "../../../../dao/jutge/GetPracticaId";
import { DeleteJunitEnviament } from "../../../../dao/jutge/DeleteJunitEnviament";
import { format, formatDistanceToNow } from 'date-fns';
import { ca } from 'date-fns/locale';
import { MdOutlineTextDecrease } from "react-icons/md";
import { PutJutgePracticaIdDocumentId } from "../../../../dao/jutge/PutJutgePracticaIdDocumentId";
import { FaRobot } from "react-icons/fa";
import ChatModal from "../../components/ChatModal";
import { PostJutgePracticaIdUsuariIdAnalyze } from "../../../../dao/jutge/PostJutgePracticaIdUsuariIdAnalyze";
import { GetJutgePracticaIdUsuariIdAnalyze } from "../../../../dao/jutge/GetJutgePracticaIdUsuariIdAnalyze";
import PracticaIdEnunciat from "./PracticaIdEnunciat";
import { GetPenalitzacionsPracticaUsuari } from "../../../../dao/jutge/GetPenalitzacionsPracticaUsuari";
import { DeletePenalitzacio } from "../../../../dao/jutge/DeletePenalitzacio";
import { GetJutgePracticaIdDocuments } from "../../../../dao/jutge/GetJutgePracticaIdDocuments";
import ModoDesafioTag from "../../activitatsId/content/ModoDesafioTag";

const { Title, Text } = Typography;
const { Content, Sider } = Layout;
const { confirm } = Modal;


interface PracticaIdTestsProps {
    id_practica?: number;
    id_usuari?: number; // Optional prop for Admin view
    id_aula?: string;
}

const PracticaIdTests: React.FC<PracticaIdTestsProps> = ({ id_practica, id_usuari, id_aula }) => {
    const getFileName = (path: string) => {
        if (!path) return "";
        return path.split(/[/\\]/).pop() || path;
    };

    const { isLoggedIn, userId } = useContext(AuthContext);
    const authUserId = userId;
    const params = useParams();
    const [enviaments, setEnviaments] = useState<any[]>([]);
    const [selectedEnviament, setSelectedEnviament] = useState<any>(null);
    const [currentFiles, setCurrentFiles] = useState<any[]>([]);
    const [junitFiles, setJunitFiles] = useState<any[]>([]);
    const [practiceDocs, setPracticeDocs] = useState<any[]>([]); // To store all docs including Enunciat
    // Documents complets (amb visible/tipo) per a la pestanya Enunciat (PracticaIdEnunciat).
    const [enunciatDocuments, setEnunciatDocuments] = useState<any[]>([]);
    // Penalitzacions lligades a (pràctica, alumne) — font de veritat (persisteixen entre reenviaments).
    const [penalitzacions, setPenalitzacions] = useState<any[]>([]);
    const [deletingPenId, setDeletingPenId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [editedFiles, setEditedFiles] = useState<{ [key: string]: string }>({});
    const [deletedFilenames, setDeletedFilenames] = useState<Set<string>>(new Set());
    const [editedJunitFiles, setEditedJunitFiles] = useState<{ [key: string]: string }>({}); // Track edits for JUnit
    const [originalFilenames, setOriginalFilenames] = useState<Set<string>>(new Set());
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [newItemName, setNewItemName] = useState("");
    const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
    const [renamingFile, setRenamingFile] = useState<{ old: string, new: string } | null>(null);

    // IA Chat state
    const [aiLoading, setAiLoading] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatRecord, setChatRecord] = useState<any>(null);

    // Identify Admin
    const { user } = useContext(AuthContext);
    const isAdmin = user?.tipus === "0" || user?.tipus === "1";

    const practicaId = id_practica ?? Number(params.id_practica);

    const effectiveUserId = id_usuari ? id_usuari + "" : authUserId + "";

    useEffect(() => {
        if (isLoggedIn && practicaId) {
            setEnviaments([]); // Clear previous user's data
            setSelectedEnviament(null);
            setCurrentFiles([]);
            setIsChatOpen(false);
            setChatRecord(null);
            loadEnviaments(practicaId + "");
            loadPracticaDetails(practicaId + "");
            loadPenalitzacions(practicaId + "");
            if (isAdmin) {
                checkExistingAiAnalysis();
            }
        }
    }, [isLoggedIn, practicaId, effectiveUserId]);

    const checkExistingAiAnalysis = async () => {
        try {
            const result = await GetJutgePracticaIdUsuariIdAnalyze((id_practica ?? practicaId) + "", effectiveUserId);
            if (result && result.questions && result.questions.length > 0) {
                const pendingQuestion = result.questions.find((q: any) => q.estat === 0);
                if (pendingQuestion) {
                    setChatRecord({
                        id: pendingQuestion.id,
                        id_aula: id_aula || '0', // use passed id_aula
                        id_practica: id_practica ?? practicaId,
                        id_problema: 0,
                        estat: 0
                    });
                    setIsChatOpen(true);
                }
            }
        } catch (e) {
            console.error("Error fetching AI analysis:", e);
        }
    };

    const loadEnviaments = async (id: string) => {
        setLoading(true);
        try {
            const data = await GetPracticaUsuarisEnviaments(id, effectiveUserId);

            // Map ID immediately and Sort
            const processed = (data || []).map((item: any) => ({
                ...item,
                id_enviament: item.id, // CRITICAL FIX: Ensure id_enviament exists
            })).sort((a: any, b: any) =>
                new Date(b.moment).getTime() - new Date(a.moment).getTime()
            );

            setEnviaments(processed);
            if (processed.length > 0) {
                setSelectedEnviament(processed[0]);
            }
        } catch (e) {
            console.error("Error loading enviaments", e);
            message.error("Error al carregar l'històric d'enviaments");
        }
        setLoading(false);
    };

    const loadPracticaDetails = async (id: string) => {
        try {
            const practica = await GetPractica(id);

            // Store all docs
            if (practica.docs) {
                setPracticeDocs(practica.docs);
            }

            // Documents complets (amb visible/tipo) des de l'endpoint dedicat, per a la
            // pestanya Enunciat (mateixa font que la vista de pràctica de l'alumne).
            try {
                const docsData = await GetJutgePracticaIdDocuments(id);
                setEnunciatDocuments(Array.isArray(docsData) ? docsData : []);
            } catch (e) {
                console.error("Error carregant documents de la pràctica", e);
            }

            let tests = practica.test;
            if (typeof tests === 'string') {
                try {
                    tests = JSON.parse(tests);
                } catch (e) {
                    console.error("Error parsing test JSON", e);
                    tests = [];
                }
            }
            setJunitFiles(Array.isArray(tests) ? tests : []);
            setEditedJunitFiles({}); // Reset edits on reload
        } catch (e) {
            console.error("Error loading practica details", e);
        }
    };

    // Carrega les penalitzacions lligades a (pràctica, alumne) des de la BD (font de veritat).
    const loadPenalitzacions = async (id: string) => {
        try {
            const data = await GetPenalitzacionsPracticaUsuari(id, effectiveUserId);
            setPenalitzacions(Array.isArray(data) ? data : []);
        } catch (e) {
            setPenalitzacions([]);
        }
    };

    // Calcula la nota efectiva aplicant les penalitzacions sobre una nota base (0-100).
    //  - Si hi ha algun OVERRIDE, preval el més baix (nota_fixada).
    //  - Si no, nota = base - sum(valor ADJUST) [valor en escala 0-100]. Clamp [0,100].
    const computeEffectiveNota = (baseNota: number): number => {
        if (!penalitzacions || penalitzacions.length === 0) return baseNota;
        let override: number | null = null;
        let totalAdjust = 0;
        for (const p of penalitzacions) {
            const tipus = String(p.tipus || 'ADJUST').toUpperCase();
            if (tipus === 'OVERRIDE') {
                const nf = p.nota_fixada != null ? Number(p.nota_fixada) : null;
                if (nf != null) override = (override == null) ? nf : Math.min(override, nf);
            } else {
                if (p.valor != null) totalAdjust += Number(p.valor);
            }
        }
        let result = override != null ? override : baseNota - totalAdjust;
        if (result < 0) result = 0;
        if (result > 100) result = 100;
        return result;
    };

    const hasOverride = penalitzacions.some(p => String(p.tipus || '').toUpperCase() === 'OVERRIDE');
    const hasIaPenalty = penalitzacions.some(p => Number(p.es_ia) === 1);

    const handleDeletePenalitzacio = async (id: number) => {
        setDeletingPenId(id);
        try {
            const ok = await DeletePenalitzacio(id);
            if (ok) {
                message.success("Penalització esborrada");
                loadPenalitzacions(practicaId + "");
            } else {
                message.error("Error esborrant la penalització");
            }
        } finally {
            setDeletingPenId(null);
        }
    };

    // Effect to fetch files when selectedEnviament changes
    useEffect(() => {
        if (selectedEnviament && selectedEnviament.id_enviament) {
            setCurrentFiles([]); // Clear previous files while loading
            setDeletedFilenames(new Set()); // Clear deletions on enviament switch
            GetEnviamentFiles(selectedEnviament.id_enviament + "").then(files => {
                if (files && files.length > 0) {
                    const names = files.map((f: any) => f.nom);
                    setOriginalFilenames(new Set(names));
                    const formatted = files.map((f: any) => ({ name: f.nom, content: f.fitxer }));
                    setCurrentFiles(formatted);
                } else {
                    // Fallback if no files returned (legacy or error), try to use 'codi' from enviament if exists
                    if (selectedEnviament.codi) {
                        setOriginalFilenames(new Set(["Main.java"]));
                        setCurrentFiles([{ name: "Main.java", content: selectedEnviament.codi }]);
                    } else {
                        setOriginalFilenames(new Set());
                        setCurrentFiles([]);
                    }
                }
            });
        } else {
            setCurrentFiles([]);
        }
    }, [selectedEnviament]);

    function decodeBase64(str: string) {
        if (!str) return "";
        // Try decoding robustly
        try {
            // Clean common prefixes if they exist (custom format)
            const clean = str.replace(/^base64:[^:]+:/, "").replace(/^base64:/, "");

            // If it doesn't look like base64 (has spaces, etc, or short without =) it might be raw text
            // But simple heuristic is difficult. If it fails atob, we return original.
            if (!/^[A-Za-z0-9+/=]+$/.test(clean.replace(/\s/g, ''))) return str;

            return decodeURIComponent(
                atob(clean)
                    .split("")
                    .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                    .join("")
            );
        } catch {
            return str;
        }
    }

    const handleFileChange = (filename: string, content: string | undefined) => {
        if (content !== undefined) {
            setEditedFiles(prev => ({ ...prev, [filename]: content }));
        }
    };

    const handleJunitChange = (docId: string, content: string | undefined) => {
        if (content !== undefined) {
            setEditedJunitFiles(prev => ({ ...prev, [docId]: content }));
        }
    };

    const toggleFileDeletion = (filename: string) => {
        setDeletedFilenames(prev => {
            const next = new Set(prev);
            if (next.has(filename)) next.delete(filename);
            else next.add(filename);
            return next;
        });
    };

    const saveJunitChanges = async (file: any) => {
        const newContent = editedJunitFiles[file.id];
        if (newContent === undefined) return; // No changes

        try {
            // Construct the updated document object
            // Note: PutJutgePracticaIdDocumentId expects the full object generally,
            // but effectively we are updating 'enunciat' field for JUnit type docs?
            // Actually 'enunciat' field holds the code for JUnit docs in the DB model usually.
            const updatedDoc = {
                ...file,
                enunciat: newContent, // The code
                nom: file.nom, // Ensure name is kept
                tipo: 'junit' // Ensure type
            };

            await PutJutgePracticaIdDocumentId(file.id, updatedDoc);
            message.success(`Fitxer ${file.nom} actualitzat correctament`);
            // Reload to ensuring sync
            loadPracticaDetails(practicaId + "");
        } catch (error) {
            console.error(error);
            message.error("Error al guardar els canvis del JUnit");
        }
    };

    const handleResubmit = async () => {
        const modifiedFilenames = Object.keys(editedFiles);
        const deletedFiles = Array.from(deletedFilenames);

        if (modifiedFilenames.length === 0 && deletedFiles.length === 0) {
            message.info("No s'ha modificat ni eliminat cap fitxer per reenviar.");
            return;
        }

        const filesToSend = modifiedFilenames.map(name => ({
            filename: name,
            content: editedFiles[name]
        }));

        confirm({
            title: 'Reenviar Enviament?',
            icon: <SyncOutlined />,
            content: `Segur que vols reenviar l'enviament modificant ${modifiedFilenames.length} fitxer(s)?`,
            onOk: async () => {
                setResending(true);
                try {
                    const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost') + ':8061';
                    const url = `${apiUrl}/jutge/practica/${practicaId}/usuaris/${effectiveUserId}/reenviar`;
                    
                    const body = {
                        idEnviamentOrigin: selectedEnviament.id_enviament,
                        // Legacy support for single file
                        filename: filesToSend[0]?.filename || "",
                        content: filesToSend[0]?.content || "",
                        // Multi-file support
                        files: filesToSend,
                        deletedFiles: deletedFiles
                    };

                    const response = await fetch(url, {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                        },
                        body: JSON.stringify(body)
                    });

                    if (response.ok) {
                        message.success("Reenviat correctament!");
                        setEditedFiles({}); // Clear edits
                        setDeletedFilenames(new Set()); // Clear deletions
                        setSelectedEnviament(null); // Clear selection to auto-select the new one
                        loadEnviaments(practicaId + "");
                    } else {
                        let errorMsg = "Error desconegut al reenviar.";
                        try {
                            const params = await response.text();
                            try {
                                const json = JSON.parse(params);
                                if (json && json.response) {
                                    errorMsg = json.response;
                                } else {
                                    errorMsg = "Error: " + response.statusText;
                                }
                            } catch (e) {
                                errorMsg = "Error backend: " + params.substring(0, 100);
                            }
                        } catch (e) {
                            console.error("Error reading response", e);
                        }
                        message.error(errorMsg);
                    }
                } catch (e) {
                    console.error(e);
                    message.error("Error de connexió o excepció frontend");
                }
                setResending(false);
            }
        });
    };

    const handleAddFile = () => {
        if (!newItemName) return;
        const name = newItemName.trim().toLowerCase().endsWith(".java") ? newItemName.trim() : `${newItemName.trim()}.java`;
        
        if (currentFiles.find(f => f.name === name)) {
            message.error("El fitxer ja existeix");
            return;
        }

        setCurrentFiles([...currentFiles, { name, content: "" }]);
        setEditedFiles(prev => ({ ...prev, [name]: "" }));
        setIsAddModalVisible(false);
        setNewItemName("");
    };

    const handleRenameFile = async () => {
        if (!renamingFile || !renamingFile.new || !selectedEnviament) return;
        const oldName = renamingFile.old;
        let newName = renamingFile.new.trim();
        if (!newName.toLowerCase().endsWith(".java")) newName += ".java";

        if (oldName === newName) {
            setIsRenameModalVisible(false);
            return;
        }

        if (currentFiles.find(f => f.name === newName)) {
            message.error("Ja existeix un fitxer amb aquest nom");
            return;
        }

        try {
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost') + ':8061';
            const url = `${apiUrl}/jutge/enviament/${selectedEnviament.id_enviament}/file/rename`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ oldName, newName })
            });

            if (response.ok) {
                message.success("Fitxer renomeat a la base de dades");
                
                // Update edited files to keep unsaved content
                setEditedFiles(prev => {
                    const next = { ...prev };
                    if (next[oldName] !== undefined) {
                        next[newName] = next[oldName];
                        delete next[oldName];
                    }
                    return next;
                });
                
                // Refresh the current enviament to get updated names locally
                setCurrentFiles(prev => prev.map(f => f.name === oldName ? { ...f, name: newName } : f));
                setOriginalFilenames(prev => {
                    const next = new Set(prev);
                    next.delete(oldName);
                    next.add(newName);
                    return next;
                });
                
                // Keep the deletedFilenames clean just in case
                setDeletedFilenames(prev => {
                    const next = new Set(prev);
                    next.delete(oldName);
                    next.delete(newName);
                    return next;
                });
            } else {
                message.error("Error al renombrar el fitxer");
            }
        } catch (e) {
            console.error(e);
            message.error("Error de xarxa");
        }

        setIsRenameModalVisible(false);
        setRenamingFile(null);
    };

    const renderSubmissionList = () => (
        <List
            itemLayout="horizontal"
            loading={loading}
            dataSource={enviaments}
            split={false}
            renderItem={(item: any) => {
                const isSelected = selectedEnviament && item.id_enviament === selectedEnviament.id_enviament;
                const parsed = item.json ? JSON.parse(item.json) : null;

                // Calculate Stats
                let passCount = 0;
                let totalCount = 0;

                if (parsed) {
                    if (parsed.results) {
                        passCount = parsed.results.filter((r: any) => r.status === 'PASSED').length;
                        totalCount = parsed.results.length;
                    }
                }

                const isOk = totalCount > 0 && passCount === totalCount;

                return (
                    <div
                        onClick={() => setSelectedEnviament(item)}
                        style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                            borderRadius: '8px',
                            marginBottom: '4px',
                            transition: 'all 0.2s',
                            borderLeft: isSelected ? `4px solid ${JutgeColors.colors.color4}` : '4px solid transparent'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Text strong style={{ fontSize: 13 }}>
                                    {formatDistanceToNow(new Date(item.moment), { addSuffix: true, locale: ca })}
                                </Text>
                                <div style={{ fontSize: 12, color: 'gray' }}>
                                    {format(new Date(item.moment), "dd/MM/yyyy HH:mm", { locale: ca })}
                                </div>
                                <div style={{ marginTop: 4 }}>
                                    <ModoDesafioTag origen={item.origen} />
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                {totalCount > 0 && (
                                    <Tag color={isOk ? "success" : "error"} style={{ marginRight: 0 }}>
                                        {passCount}/{totalCount}
                                    </Tag>
                                )}
                                {parsed?.nota !== undefined && (
                                    <div style={{ marginTop: 2 }}>
                                        <Tag color={parsed.nota >= 5 ? "blue" : "red"} style={{ marginRight: 0 }}>
                                            {Number(parsed.nota).toFixed(2)}
                                        </Tag>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }}
        />
    );

    const renderDetail = () => {
        if (!selectedEnviament) return <div style={{ padding: 40, textAlign: 'center' }}><Empty description="Selecciona un enviament" /></div>;

        const parsedJson = selectedEnviament.json ? JSON.parse(selectedEnviament.json) : null;
        const testResults = parsedJson?.results || [];
        const consoleOutput = parsedJson?.output || selectedEnviament.output;

        // Stats
        const passed = testResults.filter((t: any) => t.status === 'PASSED').length;
        const total = testResults.length;

        // Filter Enunciat Docs
        const enunciatDocs = practiceDocs.filter((d: any) => d.tipo === 'enunciat');

        // Filter Entrada/Sortida Docs
        const esDocs = practiceDocs.filter((d: any) => d.tipo === 'es');

        const items = [
            {
                key: 'files',
                label: <span><FileTextOutlined /> Codi</span>,
                children: (
                    <div style={{ height: 'calc(100vh - 250px)' }}>
                        {currentFiles.length === 0 ? (
                            <div style={{ padding: 20, textAlign: 'center' }}><Spin tip="Carregant fitxers..." /></div>
                        ) : (
                            <Tabs
                                tabPosition="top"
                                type="card"
                                style={{ height: '100%' }}
                                items={currentFiles.map((f: any, i: number) => ({
                                    key: i + "",
                                    label: (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ 
                                                textDecoration: deletedFilenames.has(f.name) ? 'line-through' : 'none', 
                                                opacity: deletedFilenames.has(f.name) ? 0.6 : 1,
                                                color: deletedFilenames.has(f.name) ? '#ff4d4f' : undefined 
                                            }}>
                                                {getFileName(f.name)}
                                            </span>
                                            {isAdmin && (
                                                <Space size={2}>
                                                    <Tooltip title="Canviar nom">
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<EditOutlined />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setRenamingFile({ old: f.name, new: f.name });
                                                                setIsRenameModalVisible(true);
                                                            }}
                                                            style={{ padding: 0, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={deletedFilenames.has(f.name) ? "Restaurar fitxer" : "Eliminar fitxer de l'enviament"}>
                                                        <Button 
                                                            type="text" 
                                                            size="small" 
                                                            danger={!deletedFilenames.has(f.name)}
                                                            icon={deletedFilenames.has(f.name) ? <UndoOutlined /> : <DeleteOutlined />} 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleFileDeletion(f.name);
                                                            }}
                                                            style={{ 
                                                                padding: 0, 
                                                                width: 20, 
                                                                height: 20, 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center' 
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Space>
                                            )}
                                        </div>
                                    ),
                                    children: (
                                        <div style={{ height: '100%', border: '1px solid #f0f0f0', borderTop: 'none', position: 'relative' }}>
                                            {deletedFilenames.has(f.name) && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0, left: 0, right: 0, bottom: 0,
                                                    background: 'rgba(255, 77, 79, 0.05)',
                                                    zIndex: 10,
                                                    pointerEvents: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Tag color="error" style={{ fontSize: 16, padding: '10px 20px' }}>FITXER MARCAT PER ELIMINAR</Tag>
                                                </div>
                                            )}
                                            <Editor
                                                height="400px"
                                                defaultLanguage="java"
                                                value={editedFiles[f.name] !== undefined ? editedFiles[f.name] : decodeBase64(f.content)}
                                                theme="vs-light"
                                                onChange={(value) => isAdmin && !deletedFilenames.has(f.name) && handleFileChange(f.name, value)}
                                                options={{
                                                    readOnly: !isAdmin || deletedFilenames.has(f.name),
                                                    minimap: { enabled: false },
                                                    fontSize: 13,
                                                    scrollBeyondLastLine: false,
                                                    padding: { top: 10 }
                                                }}
                                            />
                                        </div>
                                    )
                                }))}
                                tabBarExtraContent={isAdmin && (
                                    <Button 
                                        type="primary" 
                                        size="small" 
                                        ghost
                                        icon={<PlusOutlined />} 
                                        onClick={() => setIsAddModalVisible(true)}
                                        style={{ marginRight: 8 }}
                                    >
                                        Afegir fitxer
                                    </Button>
                                )}
                            />
                        )}
                    </div>
                )
            },
            {
                key: 'tests',
                label: <span><CheckCircleFilled /> Tests ({passed}/{total})</span>,
                children: (
                    <div style={{ height: 'calc(100vh - 250px)', overflow: 'auto', padding: '0 8px' }}>
                        {testResults.length === 0 ? <Empty description="No hi ha resultats de tests" /> : (
                            <List
                                dataSource={testResults}
                                renderItem={(item: any) => {
                                    const isPass = item.status === 'PASSED';
                                    return (
                                        <div style={{
                                            background: '#fff',
                                            marginBottom: 8,
                                            padding: 16,
                                            borderRadius: 8,
                                            border: '1px solid #f0f0f0',
                                            borderLeft: `4px solid ${isPass ? '#52c41a' : '#ff4d4f'}`
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <Text strong>{getFileName(item.test)}</Text>
                                                <Tag color={isPass ? 'success' : 'error'}>{item.status}</Tag>
                                            </div>
                                            {!isPass && (
                                                <div style={{ marginTop: 8, fontSize: 13 }}>
                                                    {item.message && <div style={{ color: '#cf1322', marginBottom: 4 }}>{item.message}</div>}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                                                        <div style={{ background: '#fafafa', padding: 8, borderRadius: 4 }}>
                                                            <Text type="secondary" style={{ fontSize: 11 }}>ESPERAT</Text>
                                                            <pre style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>{item.expected}</pre>
                                                        </div>
                                                        <div style={{ background: '#fff1f0', padding: 8, borderRadius: 4 }}>
                                                            <Text type="secondary" style={{ fontSize: 11 }}>OBTINGUT</Text>
                                                            <pre style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>{item.obtained}</pre>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        )}
                    </div>
                )
            },
            ...(parsedJson?.adaptations && parsedJson.adaptations.length > 0 ? [{
                key: 'adaptacions',
                label: <span><ToolOutlined /> Adaptacions Automàtiques ({parsedJson.adaptations.length})</span>,
                children: (
                    <div style={{ height: 'calc(100vh - 250px)', overflow: 'auto', padding: '0 8px' }}>
                        <Alert 
                            message="S'han detectat problemes de compilació que s'han intentat solucionar automàticament." 
                            type="warning" 
                            showIcon 
                            style={{ marginBottom: 16 }} 
                        />
                        <List
                            dataSource={parsedJson.adaptations}
                            renderItem={(item: any) => {
                                const isTypo = item.type.includes('TYPO');
                                return (
                                    <div style={{
                                        background: '#fff',
                                        marginBottom: 8,
                                        padding: 16,
                                        borderRadius: 8,
                                        border: '1px solid #f0f0f0',
                                        borderLeft: `4px solid ${isTypo ? '#faad14' : '#1890ff'}`
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <Text strong>{isTypo ? 'Correcció d\'Error Tipogràfic (Typo)' : 'Mètode/Classe Faltant (Stub)'}</Text>
                                            <Tag color={isTypo ? 'warning' : 'blue'}>{item.type}</Tag>
                                        </div>
                                        <div style={{ marginTop: 8, fontSize: 13 }}>
                                            <div style={{ marginBottom: 4 }}>
                                                <Text type="secondary">El test esperava trobar:</Text>
                                                <div style={{ background: '#fafafa', padding: 8, borderRadius: 4, fontFamily: 'monospace', marginTop: 4 }}>
                                                    {item.expected}
                                                </div>
                                            </div>
                                            {isTypo && item.found && (
                                                <div style={{ marginBottom: 4 }}>
                                                    <Text type="secondary">S'ha mapejat a la teva implementació:</Text>
                                                    <div style={{ background: '#e6f7ff', padding: 8, borderRadius: 4, fontFamily: 'monospace', marginTop: 4 }}>
                                                        {item.found}
                                                    </div>
                                                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                                                        Similitud detectada: {Math.round(item.similarity * 100)}%
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </div>
                )
            }] : []),
            {
                key: 'console',
                label: <span><ConsoleSqlOutlined /> Sortida</span>,
                children: (
                    <div style={{
                        background: '#1e1e1e',
                        color: '#d4d4d4',
                        padding: 16,
                        borderRadius: 8,
                        fontFamily: "'Fira Code', monospace",
                        height: 'calc(100vh - 250px)',
                        overflow: 'auto',
                        fontSize: 13
                    }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{consoleOutput || "// Sense sortida de consola"}</pre>
                    </div>
                )
            },
            {
                key: 'unit_tests',
                label: <span><ExperimentOutlined /> Tests Unitaris</span>,
                children: (
                    <div style={{ height: 'calc(100vh - 250px)' }}>
                        {junitFiles.length === 0 ? (
                            <Empty description="No hi ha fitxers de tests unitarios" />
                        ) : (
                            <Tabs
                                tabPosition="top"
                                type="card"
                                style={{ height: '100%' }}
                                items={junitFiles.map((f: any) => ({
                                    key: f.id + "",
                                    label: f.nom,
                                    children: (
                                        <div style={{ height: '100%', border: '1px solid #f0f0f0', borderTop: 'none', display: 'flex', flexDirection: 'column' }}>
                                            {isAdmin && (
                                                <div style={{ padding: '5px', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', textAlign: 'right' }}>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        icon={<SaveOutlined />}
                                                        onClick={() => saveJunitChanges(f)}
                                                        disabled={editedJunitFiles[f.id] === undefined} // Disable if not edited
                                                    >
                                                        Guardar Canvis
                                                    </Button>
                                                </div>
                                            )}
                                            <Editor
                                                height="400px"
                                                defaultLanguage="java"
                                                value={editedJunitFiles[f.id] !== undefined ? editedJunitFiles[f.id] : (f.enunciat || "")}
                                                theme="vs-light"
                                                onChange={(value) => isAdmin && handleJunitChange(f.id, value)}
                                                options={{
                                                    readOnly: !isAdmin,
                                                    minimap: { enabled: false },
                                                    fontSize: 13,
                                                    scrollBeyondLastLine: false,
                                                    padding: { top: 10 }
                                                }}
                                            />
                                        </div>
                                    )
                                }))}
                            />
                        )}
                    </div>
                )
            },
            ...(isAdmin ? [{
                key: 'ia_analisi',
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: aiLoading ? '#10a37f' : undefined }}>
                            <FaRobot style={{ marginRight: 4 }} />
                            {aiLoading ? 'Analitzant...' : 'IA Anàlisi'}
                        </span>
                        {chatRecord && (
                            <Tooltip title="Nova / Reset Anàlisi">
                                <ReloadOutlined 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setChatRecord(null);
                                        setIsChatOpen(false);
                                    }} 
                                    style={{ color: '#999', cursor: 'pointer' }}
                                />
                            </Tooltip>
                        )}
                    </div>
                ),
                children: (
                    <div style={{ height: 'calc(100vh - 250px)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
                        {isChatOpen && chatRecord && (
                            <div style={{ width: '100%', height: '100%' }}>
                                <ChatModal
                                    modalVisible={isChatOpen}
                                    setModalVisible={setIsChatOpen}
                                    selectedRecord={chatRecord}
                                    practica={true}
                                    onClose={() => setIsChatOpen(false)}
                                    inline={true}
                                />
                            </div>
                        )}
                        {!isChatOpen && chatRecord ? (
                            <>
                                <FaRobot size={48} style={{ color: '#10a37f' }} />
                                <div style={{ textAlign: 'center', color: '#555' }}>
                                    Ja hi ha una conversa amb la IA oberta per a aquest alumne.<br />
                                </div>
                                <Button
                                    type="primary"
                                    icon={<FaRobot />}
                                    style={{ background: '#10a37f', borderColor: '#10a37f' }}
                                    onClick={() => setIsChatOpen(true)}
                                >
                                    Obrir conversa actual
                                </Button>
                            </>
                        ) : !isChatOpen && !chatRecord ? (
                            <>
                                <FaRobot size={48} style={{ color: '#10a37f', opacity: 0.7 }} />
                                <div style={{ textAlign: 'center', color: '#555' }}>
                                    Analitza el codi d'aquest alumno amb la IA.<br />
                                    <span style={{ fontSize: 12, color: '#999' }}>S'enviarà el codi, tests i sortida de consola automàticament.</span>
                                </div>
                                <Button
                                    type="primary"
                                    icon={<FaRobot />}
                                    loading={aiLoading}
                                    style={{ background: '#10a37f', borderColor: '#10a37f' }}
                                    onClick={async () => {
                                        if (!selectedEnviament) { message.warning('Selecciona un enviament primer'); return; }
                                        const parsedJson = selectedEnviament.json ? JSON.parse(selectedEnviament.json) : null;
                                        const testResults = parsedJson?.results || [];
                                        const consoleOutput = parsedJson?.output || selectedEnviament.output || '';
                                        const passed = testResults.filter((t: any) => t.status === 'PASSED').length;
                                        const total = testResults.length;

                                        // Build file context from currentFiles
                                        let filesMd = currentFiles.map((f: any) => {
                                            const content = decodeBase64(f.content);
                                            return `**${f.name}**\n\`\`\`java\n${content}\n\`\`\``;
                                        }).join('\n\n') || 'Sin código.';

                                        let testsMd = testResults.map((r: any) =>
                                            `- Test: ${r.test || r.name || 'Desconocido'} | Estado: ${r.status}${ r.message ? ' | ' + r.message : '' }`
                                        ).join('\n') || 'Sin tests estructurados.';

                                        const outputMd = consoleOutput ? `\`\`\`text\n${consoleOutput}\n\`\`\`` : 'Sin salida de consola.';

                                        const context = `El professor vol analitzar el codi d'aquest alumne.\n\n### Fitxers de l'alumne:\n${filesMd}\n\n### Resultats de Tests (Correctes: ${passed}, Incorrectes: ${total - passed}):\n${testsMd}\n\n### Sortida de consola:\n${outputMd}`;

                                        setAiLoading(true);
                                        try {
                                            const result = await PostJutgePracticaIdUsuariIdAnalyze(
                                                (id_practica ?? practicaId) + '',
                                                (id_usuari ?? authUserId) + '',
                                                context,
                                                id_aula || selectedEnviament.id_aula || '0',
                                                userId
                                            );
                                            if (result && result.questions && result.questions.length > 0) {
                                                const newQuestion = result.questions.reduce((prev: any, current: any) => (prev.id > current.id) ? prev : current);
                                                setChatRecord({
                                                    id: newQuestion.id,
                                                    id_aula: id_aula || selectedEnviament.id_aula || '0',
                                                    id_practica: id_practica ?? practicaId,
                                                    id_problema: 0,
                                                    estat: newQuestion.estat
                                                });
                                                setIsChatOpen(true);
                                            } else {
                                                message.error('Error en iniciar la sessió IA');
                                            }
                                        } catch (e) {
                                            message.error('Error de connexió amb la IA');
                                        } finally {
                                            setAiLoading(false);
                                        }
                                    }}
                                >
                                    {aiLoading ? 'Analitzant...' : 'Analitzar amb IA'}
                                </Button>
                            </>
                        ) : null}
                    </div>
                )
            }] : []),
            {
                key: 'penalties',
                label: <span><MdOutlineTextDecrease /> Penalitzacions{penalitzacions.length > 0 ? ` (${penalitzacions.length})` : ''}</span>,
                children: (
                    <div style={{ height: 'calc(100vh - 250px)', overflow: 'auto', padding: '0 8px' }}>
                        {penalitzacions.length === 0 ? (
                            <Empty description="No hi ha penalitzacions aplicades" />
                        ) : (
                            <List
                                dataSource={penalitzacions}
                                renderItem={(item: any) => {
                                    const tipus = String(item.tipus || 'ADJUST').toUpperCase();
                                    const esIa = Number(item.es_ia) === 1;
                                    const isOverride = tipus === 'OVERRIDE';
                                    // ADJUST: valor en escala 0-100; es mostra en punts de l'alumne (/10).
                                    const valorNum = item.valor != null ? Number(item.valor) : 0;
                                    const isPenalty = valorNum > 0;
                                    const notaFixadaNum = item.nota_fixada != null ? Number(item.nota_fixada) : 0;

                                    const borderColor = esIa
                                        ? '#722ed1'
                                        : (isOverride ? '#fa8c16' : (isPenalty ? '#ff4d4f' : '#52c41a'));

                                    let titol: React.ReactNode;
                                    if (esIa) {
                                        titol = <span><FaRobot style={{ marginRight: 6 }} />Suspès per ús d'IA generativa (nota fixada: {notaFixadaNum})</span>;
                                    } else if (isOverride) {
                                        titol = `Nota fixada: ${notaFixadaNum}`;
                                    } else {
                                        const punts = Math.abs(valorNum) / 10;
                                        titol = isPenalty ? `Penalització: -${punts} punts` : `Bonificació: +${punts} punts`;
                                    }

                                    return (
                                        <div style={{
                                            background: esIa ? '#f9f0ff' : '#fff',
                                            marginBottom: 8,
                                            padding: 16,
                                            borderRadius: 8,
                                            border: '1px solid #f0f0f0',
                                            borderLeft: `4px solid ${borderColor}`
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                                <Text strong style={{ fontSize: 15, color: esIa ? '#722ed1' : undefined }}>
                                                    {titol}
                                                </Text>
                                                <Space size="small">
                                                    {item.moment && (
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            {format(new Date(item.moment), "PPP p", { locale: ca })}
                                                        </Text>
                                                    )}
                                                    {isAdmin && (
                                                        <Tooltip title="Esborrar penalització">
                                                            <Button
                                                                danger
                                                                type="text"
                                                                size="small"
                                                                icon={<DeleteOutlined />}
                                                                loading={deletingPenId === item.id}
                                                                onClick={() => handleDeletePenalitzacio(item.id)}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Space>
                                            </div>
                                            {item.motiu && (
                                                <div style={{ marginTop: 8 }}>
                                                    <Text>{item.motiu}</Text>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        )}
                    </div>
                )
            },
            ...(esDocs.length > 0 ? [{
                key: 'exemplos_es',
                label: <span><UnorderedListOutlined /> Exemples E/S</span>,
                children: (
                    <div style={{ height: 'calc(100vh - 250px)' }}>
                        <Tabs
                            tabPosition="top"
                            type="card"
                            style={{ height: '100%' }}
                            items={esDocs.map((doc: any) => ({
                                key: doc.id + "",
                                label: doc.nom || `Exemple ${doc.id}`,
                                children: (
                                    <div style={{ height: '100%', border: '1px solid #f0f0f0', borderTop: 'none' }}>
                                        <Editor
                                            height="400px"
                                            defaultLanguage="text"
                                            value={decodeBase64(doc.enunciat)}
                                            theme="vs-light"
                                            options={{
                                                readOnly: true,
                                                minimap: { enabled: false },
                                                fontSize: 13,
                                                scrollBeyondLastLine: false,
                                                padding: { top: 10 },
                                                wordWrap: "on"
                                            }}
                                        />
                                    </div>
                                )
                            }))}
                        />
                    </div>
                )
            }] : [])
        ];

        // Pestanya "Enunciat" al principi: mostra el component complet PracticaIdEnunciat
        // (enunciat + TOTS els documents associats: E/S, tests, codi...), en mode
        // visualització. Es mostra si hi ha qualsevol document associat.
        if (enunciatDocuments.length > 0) {
            items.unshift({
                key: 'enunciat',
                label: <span><FileTextOutlined /> Enunciat</span>,
                children: (
                    <div style={{ height: 'calc(100vh - 250px)', overflow: 'auto', padding: '16px', background: '#fff' }}>
                        <PracticaIdEnunciat
                            documents={enunciatDocuments}
                            propIdPractica={practicaId + ""}
                            defaultViewMode="preview"
                            onRefresh={() => loadPracticaDetails(practicaId + "")}
                        />
                    </div>
                )
            });
        }

        return (
            <div style={{ padding: '0 24px' }}>
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <Title level={4} style={{ margin: 0 }}>
                                Enviament #{selectedEnviament.id_enviament}
                            </Title>
                            <ModoDesafioTag origen={selectedEnviament.origen} />
                            {isAdmin && (
                                <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    size="small"
                                    onClick={() => handleDelete(selectedEnviament.id_enviament)}
                                >
                                    Eliminar
                                </Button>
                            )}
                        </div>
                        <Text type="secondary">{format(new Date(selectedEnviament.moment), "PPP p", { locale: ca })}</Text>
                        {(parsedJson?.nota !== undefined) && (() => {
                            // Nota base dels tests (preferim notaBase si existeix; si no, la nota del json).
                            const base = parsedJson.notaBase !== undefined ? Number(parsedJson.notaBase) : Number(parsedJson.nota);
                            const efectiva = computeEffectiveNota(base);
                            const penalitzada = Math.abs(efectiva - base) > 0.001;
                            return (
                                <Space style={{ marginLeft: 16 }} size="small">
                                    <Tag color={penalitzada ? (hasIaPenalty ? 'purple' : 'volcano') : 'geekblue'} style={{ fontSize: 16, padding: '4px 10px', margin: 0 }}>
                                        Nota: {efectiva.toFixed(2)}
                                    </Tag>
                                    {penalitzada && (
                                        <Tooltip title={hasOverride ? "Nota fixada per una penalització (sobreescriu la nota dels tests)" : "Nota ajustada per penalitzacions"}>
                                            <Tag color="default" style={{ margin: 0, fontSize: 12 }}>
                                                base tests: {base.toFixed(2)}
                                            </Tag>
                                        </Tooltip>
                                    )}
                                    {hasIaPenalty && (
                                        <Tag color="purple" icon={<FaRobot style={{ marginRight: 4 }} />} style={{ margin: 0 }}>IA</Tag>
                                    )}
                                </Space>
                            );
                        })()}
                        {isAdmin && (
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleResubmit}
                                loading={resending}
                                disabled={Object.keys(editedFiles).length === 0 && deletedFilenames.size === 0}
                                style={{ marginLeft: 16 }}
                            >
                                Reenviar Canvis
                            </Button>
                        )}
                    </div>
                </div>

                {/* Avís destacat de detecció d'IA, visible dalt de tot abans de les pestanyes */}
                {(() => {
                    const iaPen = penalitzacions.find((p: any) => Number(p.es_ia) === 1);
                    if (!iaPen) return null;
                    return (
                        <Alert
                            type="error"
                            showIcon
                            banner
                            style={{ marginBottom: 16, borderRadius: 8 }}
                            message={<span style={{ fontWeight: 700 }}>Ús d'IA generativa detectat</span>}
                            description={iaPen.motiu}
                        />
                    );
                })()}

                <Tabs defaultActiveKey={enunciatDocuments.length > 0 ? "enunciat" : "files"} items={items} type="line" size="large" />
            </div>
        );
    };

    const handleDelete = (id: string) => {
        confirm({
            title: 'Eliminar enviament?',
            icon: <DeleteOutlined />,
            content: 'Estàs segur que vols eliminar aquest enviament? Aquesta acció no es pot desfer.',
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancel·lar',
            onOk: () => {
                DeleteJunitEnviament(id).then(() => {
                    message.success("Enviament eliminat correctament");
                    loadEnviaments(practicaId + "");
                    setSelectedEnviament(null);
                }).catch((err) => {
                    console.error(err);
                    message.error("Error al eliminar l'enviament");
                });
            }
        });
    };

    return (
        <Layout style={{ background: '#fff', minHeight: '80vh' }}>
            <Sider width={280} theme="light" style={{ borderRight: '1px solid #f0f0f0', padding: '16px 8px' }}>
                <Title level={5} style={{ paddingLeft: 8, marginBottom: 16 }}>Històric</Title>
                <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                    {renderSubmissionList()}
                </div>
            </Sider>
            <Content style={{ background: '#fff', paddingTop: 24 }}>
                {renderDetail()}
            </Content>

            {/* Modals for Add/Rename */}
            <Modal
                title="Afegir nou fitxer"
                open={isAddModalVisible}
                onOk={handleAddFile}
                onCancel={() => { setIsAddModalVisible(false); setNewItemName(""); }}
                okText="Afegir"
                cancelText="Cancel·lar"
            >
                <div style={{ marginBottom: 10 }}>Introdueix el nom del nou fitxer Java:</div>
                <Input 
                    placeholder="NomFitxer.java" 
                    value={newItemName} 
                    onChange={(e) => setNewItemName(e.target.value)}
                    onPressEnter={handleAddFile}
                    autoFocus
                />
            </Modal>

            <Modal
                title="Canviar nom del fitxer"
                open={isRenameModalVisible}
                onOk={handleRenameFile}
                onCancel={() => { setIsRenameModalVisible(false); setRenamingFile(null); }}
                okText="Canviar"
                cancelText="Cancel·lar"
            >
                <div style={{ marginBottom: 10 }}>Canvia el nom del fitxer <strong>{renamingFile?.old}</strong>:</div>
                <Input 
                    placeholder="NouNom.java" 
                    value={renamingFile?.new} 
                    onChange={(e) => setRenamingFile(prev => prev ? { ...prev, new: e.target.value } : null)}
                    onPressEnter={handleRenameFile}
                    autoFocus
                />
            </Modal>
        </Layout>
    );

};

const EditableEnunciat = ({ doc, isAdmin, refresh }: { doc: any, isAdmin: boolean, refresh: () => void }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState("");
    const ReactQuill = require('react-quill'); // Require to avoid TS issues if types missing, or use import if confirmed. using require for safety as in other files potentially.
    require('react-quill/dist/quill.snow.css');

    // Decode function duplicated for local scope simplicity or move to utils in future
    function decodeBase64(str: string) {
        if (!str) return "";
        try {
            const clean = str.replace(/^base64:[^:]+:/, "").replace(/^base64:/, "");
            if (!/^[A-Za-z0-9+/=]+$/.test(clean.replace(/\s/g, ''))) return str;
            return decodeURIComponent(
                atob(clean).split("").map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
            );
        } catch { return str; }
    }

    // Initialize content
    useEffect(() => {
        setContent(decodeBase64(doc.enunciat));
    }, [doc]);

    const handleSave = async () => {
        try {
            // Send raw content (HTML/Text) directly, similar to EditDocumentModal.
            // The Decode function in PracticaIdTests handles raw strings if they don't match Base64 regex.
            const updatedDoc = {
                ...doc,
                enunciat: content,
                tipo: 'enunciat'
            };

            await PutJutgePracticaIdDocumentId(doc.id, updatedDoc);
            message.success("Enunciat actualitzat");
            setIsEditing(false);
            refresh();
        } catch (e) {
            console.error(e);
            message.error("Error al guardar enunciat");
        }
    };

    return (
        <div style={{ marginBottom: 32, border: isEditing ? '1px solid #1890ff' : '1px solid transparent', padding: isEditing ? 16 : 0, borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                {doc.nom && <Title level={5} style={{ margin: 0 }}>{doc.nom}</Title>}
                {isAdmin && !isEditing && (
                    <Button type="link" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>Editar</Button>
                )}
                {isAdmin && isEditing && (
                    <div>
                        <Button onClick={() => setIsEditing(false)} style={{ marginRight: 8 }}>Cancel·lar</Button>
                        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Guardar</Button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <ReactQuill value={content} onChange={setContent} theme="snow" />
            ) : (
                <div dangerouslySetInnerHTML={{ __html: decodeBase64(doc.enunciat) }} />
            )}
        </div>
    );
};

export default PracticaIdTests;

